"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.uninitialisedError = exports.default = exports.ThemeSetterContext = exports.ThemeContext = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _utils = require("./utils");
var _responsiveProps = _interopRequireDefault(require("../utils/props/responsiveProps"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const uninitialisedError = exports.uninitialisedError = new Error('Theme context used outside of ThemeProvider');
const ThemeContext = exports.ThemeContext = /*#__PURE__*/_react.default.createContext(uninitialisedError);
const ThemeSetterContext = exports.ThemeSetterContext = /*#__PURE__*/_react.default.createContext(uninitialisedError);

// These options default to `true` in v1.x to match legacy defaults and avoid breaking changes.
// This should change in future major releases to become "opt-in" legacy support.
const defaultThemeOptions = {
  // TODO: switch `forceAbsoluteFontSizing` to be false by default in the next major version
  forceAbsoluteFontSizing: true,
  // TODO: switch `forceZIndex` to be false by default in the next major version
  forceZIndex: true,
  // TODO: switch `enableHelmetSSR` to be false by default in the next major version
  enableHelmetSSR: true,
  enableMediaQueryStyleSheet: false
};
const ThemeProvider = _ref => {
  let {
    children,
    defaultTheme,
    themeOptions = {}
  } = _ref;
  const [theme, setTheme] = _react.default.useState(defaultTheme);
  _react.default.useEffect(() => {
    if (theme.metadata.name !== defaultTheme.metadata.name) {
      setTheme(defaultTheme);
    }
  }, [theme.metadata.name, defaultTheme]);
  const appliedThemeOptions = {
    ...defaultThemeOptions,
    ...themeOptions
  };

  // Validate the theme tokens version on every render.
  // This will intentionally break the application when attempting to use an invalid theme.
  // This will surface an incompatibility quickly rather than allowing the potential for strange bugs due to missing or incompatible tokens.
  (0, _utils.validateThemeTokensVersion)(theme);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(ThemeSetterContext.Provider, {
    value: setTheme,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(ThemeContext.Provider, {
      value: {
        ...theme,
        themeOptions: appliedThemeOptions
      },
      children: children
    })
  });
};
ThemeProvider.propTypes = {
  children: _propTypes.default.node.isRequired,
  defaultTheme: _propTypes.default.shape({
    metadata: _propTypes.default.shape({
      themeTokensVersion: _propTypes.default.string.isRequired,
      name: _propTypes.default.string.isRequired
    }).isRequired
  }).isRequired,
  /**
   * An object containing options allowing to customize the theming experience:
   *
   * - `forceAbsoluteFontSizing`: available on web only; when set to true, allows
   *   using absolute font sizing (in pixels, doesn't scale) instead of the
   *   relative sizing (in `rem`, scales depending on the browser settings)
   * - `contentMaxWidth`: allows configuration of the content max width to be used in components
   *   such as Footnote and Notification to avoid content to stretch width more then the page's width
   * - `forceZIndex`: available on web only, when set to false, sets zIndex on `View` to be `auto`
   *   and when true, sets zIndex to be `0` (the default from `react-native-web`)
   * - `enableHelmetSSR`: on Web SSR, allows React Helmet to run during server-side rendering. This should be
   *   disabled unless a web app has been specifically configured to stop React Helmet accumulating
   *   instances (which may cause a memory leak). See React Helmet's docs: https://github.com/nfl/react-helmet
   * - `enableMediaQueryStyleSheet`: enables the use of Media Query StyleSheet.
   */
  themeOptions: _propTypes.default.shape({
    forceAbsoluteFontSizing: _propTypes.default.bool,
    forceZIndex: _propTypes.default.bool,
    enableHelmetSSR: _propTypes.default.bool,
    contentMaxWidth: _responsiveProps.default.getTypeOptionallyByViewport(_propTypes.default.number),
    enableMediaQueryStyleSheet: _propTypes.default.bool
  })
};
var _default = exports.default = ThemeProvider;